import type { IconProps } from '@phosphor-icons/react';
import {
  EnvelopeIcon,
  FileCodeIcon,
  FileHtmlIcon,
  FileIcon,
  FileMdIcon,
  FilePptIcon,
  FileSvgIcon,
  FileTxtIcon,
  FileXlsIcon,
  MicrosoftWordLogoIcon,
  PackageIcon,
} from '@phosphor-icons/react';
import type { ComponentType } from 'react';

import { FileTypes, fileTypeControllers } from '@/shared/constants';
import { fileTypeIcons } from '@/shared/uiConstants';

type AttachmentIconComponent = ComponentType<IconProps>;

/** Icons for `FileTypes` media kinds — same Video/Audio/Image glyphs as provenance rows. */
const KIND_ICON: Record<string, AttachmentIconComponent> = {
  video: fileTypeIcons.Video,
  audio: fileTypeIcons.Audio,
  image: fileTypeIcons.Image,
  pdf: fileTypeIcons.PDF,
  spreadsheet: fileTypeIcons.Spreadsheet,
  docx: fileTypeIcons['MS Office'],
  pptx: FilePptIcon,
  markdown: FileMdIcon,
  html: FileHtmlIcon,
  text: FileTxtIcon,
  json: FileCodeIcon,
};

/**
 * Glyphs that are more specific than a `FileTypes` kind (or not listed there).
 * Checked before kind lookup so `.xlsx` stays FileXls rather than the generic table icon.
 */
const EXTENSION_OVERRIDE: Record<string, AttachmentIconComponent> = {
  '.rtf': MicrosoftWordLogoIcon,
  '.odt': MicrosoftWordLogoIcon,
  '.xlsx': FileXlsIcon,
  '.xls': FileXlsIcon,
  '.ods': FileXlsIcon,
  '.odp': FilePptIcon,
  '.svg': FileSvgIcon,
  '.xml': FileCodeIcon,
  '.yaml': FileCodeIcon,
  '.yml': FileCodeIcon,
  '.drawio': FileCodeIcon,
  '.dot': FileCodeIcon,
  '.gv': FileCodeIcon,
  '.mmd': FileCodeIcon,
  '.puml': FileCodeIcon,
  '.plantuml': FileCodeIcon,
  '.graphml': FileCodeIcon,
  '.vsdx': FileCodeIcon,
  '.zip': PackageIcon,
};

const EMAIL_EXTENSIONS = new Set(
  fileTypeControllers.Email.filter((ext) => ext !== 'pdfa').map((ext) => ext.toLowerCase()),
);

function extensionFromFilename(filename: string): string | null {
  const dotIndex = filename.lastIndexOf('.');
  if (dotIndex < 0) return null;
  return filename.slice(dotIndex).toLowerCase();
}

export function chatAttachmentIconForFilename(filename: string): AttachmentIconComponent {
  const extension = extensionFromFilename(filename);
  if (!extension) return FileIcon;

  const override = EXTENSION_OVERRIDE[extension];
  if (override) return override;

  const extNoDot = extension.slice(1);
  if (EMAIL_EXTENSIONS.has(extNoDot)) return EnvelopeIcon;

  const kind = (FileTypes as Record<string, string>)[extNoDot];
  if (kind && KIND_ICON[kind]) return KIND_ICON[kind];

  return FileIcon;
}

type ChatAttachmentFileIconProps = {
  filename: string;
  size?: number;
  weight?: IconProps['weight'];
};

export function ChatAttachmentFileIcon({
  filename,
  size = 14,
  weight = 'light',
}: ChatAttachmentFileIconProps) {
  const Icon = chatAttachmentIconForFilename(filename);
  return <Icon size={size} weight={weight} aria-hidden />;
}
